import {
  ArrowLeftRight,
  ArrowRight,
  Banknote,
  BarChart3,
  Building,
  Coins,
  CreditCard,
  FileText,
  MapPin,
  PiggyBank,
  Plug,
  Tags,
  Users,
  Wallet,
} from 'lucide-react'
import { Suspense, lazy, useState } from 'react'
import { useLocation } from 'react-router'

import {
  isApproverOnlyRole,
  isPrivilegedRole,
  isSpenderOnlyRole,
  minimumRequiredRole,
} from '@ankor-io/common/domain/iam/account'
import { Role } from '@ankor-io/common/types/account'
import { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import { FeatureToggleValue } from '@ankor-io/common/types/system'
import { Badge } from '@ankor-io/ui-components/src/Badge'
import { Divider } from '@ankor-io/ui-components/src/Divider'
import {
  ExpandableMenu,
  ExpandableMenuChevron,
  ExpandableMenuItem,
  ExpandableMenuPanel,
  ExpandableMenuTrigger,
} from '@ankor-io/ui-components/src/ExpandableMenu'
import { Link } from '@ankor-io/ui-components/src/Link'
import { Navbar, NavbarDivider, NavbarSection, NavbarSpacer } from '@ankor-io/ui-components/src/Navbar'
import { Sidebar, SidebarBody, SidebarFooter, SidebarHeader, SidebarLabel } from '@ankor-io/ui-components/src/Sidebar'
import { SidebarLayout } from '@ankor-io/ui-components/src/SidebarLayout'
import { Tooltip } from '@ankor-io/ui-components/src/Tooltip'

import { useModuleAvailabilityQuery } from '@/api/queries/useVesselQuery'
import AnkorLogo from '@/assets/AnkorLogoGradient.svg'
import { useConnectedAccount } from '@/providers/connectedAccount/useConnectedAccount'
import { useFeatureToggle } from '@/providers/featureToggle/useFeatureToggle'
import { useUserContext } from '@/providers/user/useUserContext'
import { type ModuleCTAContent, getModuleContent } from '@/utils/moduleKycState'
import { type NavItem, SidebarNavLinks, SidebarNavSection } from '@/views/components/SidebarNavSection'
import UserAvatarMenu from '@/views/components/UserAvatarMenu'
import { VesselDropdown } from '@/views/components/VesselDropdown'

const OrganizationsModal = lazy(() => import('@/views/components/OrganizationsModal/OrganizationsModal'))
const ActivateModuleModal = lazy(() =>
  import('./ActivateModuleModal').then((m) => ({ default: m.ActivateModuleModal })),
)

type SectionCtaProps = {
  cta: ModuleCTAContent | null
  color: 'primary' | 'orange'
  onActivate?: () => void
}

const ctaColors = {
  orange: 'bg-orange-500/85 hover:bg-orange-500/95 text-white dark:bg-orange-500/85 dark:hover:bg-orange-500/95',
  primary: 'bg-blue-600/85 hover:bg-blue-600/95 text-white dark:bg-blue-600/85 dark:hover:bg-blue-600/95',
} as const

const SectionCta = ({ cta, color, onActivate }: SectionCtaProps): React.ReactNode => {
  if (!cta) return null

  const { label, to, activateModule } = cta
  const classes = `mb-1 flex w-full items-center justify-center gap-1.5 rounded-sm px-3 py-1.5 text-xs font-semibold transition-colors ${ctaColors[color]}`

  if (to) {
    return (
      <Link
        to={to}
        className={classes}
      >
        {label}
        <ArrowRight className="size-3.5" />
      </Link>
    )
  }

  if (activateModule) {
    return (
      <button
        type="button"
        onClick={onActivate}
        className={`cursor-pointer ${classes}`}
      >
        {label}
        <ArrowRight className="size-3.5" />
      </button>
    )
  }

  return (
    <Badge
      color={color}
      className="mb-1 w-full justify-center italic font-light!"
    >
      {label}
    </Badge>
  )
}

/**
 * Restricted roles only ever see a subset of the module navigation:
 * - Spenders: Expenses only.
 * - Approvers: Dashboard + Expenses only (no management surfaces).
 * Everyone else (VESSEL_ADMIN and above) sees the full list, with management
 * items disabled below VESSEL_ADMIN.
 */
const restrictNavItems = (items: NavItem[], allowedUrls: string[]): NavItem[] =>
  items.filter((item) => allowedUrls.includes(item.url))

export const Layout = ({ children }: { children: React.ReactNode }) => {
  const { orgFeatureToggles } = useFeatureToggle()
  const { organizations, activeOrganizationId, activeVesselId, activeOrganizationRole } = useUserContext()

  const location = useLocation()
  const { data: moduleAvailability } = useModuleAvailabilityQuery({ vesselId: activeVesselId })
  const [isOrganizationsModalOpen, setIsOrganizationsModalOpen] = useState(false)
  const [activateModuleType, setActivateModuleType] = useState<AwxConnectedAccountType | null>(null)
  const activeOrganization = activeOrganizationId ? organizations[activeOrganizationId] || null : null

  const { connectedAccounts, refreshConnectedAccounts } = useConnectedAccount()

  const opsModule = moduleAvailability?.modules.find((m) => m.type === AwxConnectedAccountType.OPERATIONS) ?? null
  const apaModule = moduleAvailability?.modules.find((m) => m.type === AwxConnectedAccountType.APA) ?? null

  const opsConnectedAccount = connectedAccounts.find((ca) => ca.type === AwxConnectedAccountType.OPERATIONS) ?? null
  const apaConnectedAccount = connectedAccounts.find((ca) => ca.type === AwxConnectedAccountType.APA) ?? null

  const UNNAMED_ORGANIZATION = 'Unnamed Organization'

  const opsContent = getModuleContent(opsModule, opsConnectedAccount)
  const apaContent = getModuleContent(apaModule, apaConnectedAccount)

  /*
   Menus follow the same logic as section content:
   - no vessel => disabled;
   - module not ACTIVE (KYC incomplete, suspended, etc.) => disabled;
   - else enabled (with role checks)
  */

  const opsSectionDisabled = !activeVesselId || !opsContent.dashboardEnabled
  const opsMenuDisabled = !activeVesselId || !opsContent.menuItemsEnabled
  const apaSectionDisabled = !activeVesselId || !apaContent.dashboardEnabled
  const apaMenuDisabled = !activeVesselId || !apaContent.menuItemsEnabled
  const moduleTooltipWhenNoVessel = 'Select a vessel to continue'
  const opsItemTooltip =
    opsSectionDisabled || opsMenuDisabled
      ? (opsContent.tooltip ?? (!activeVesselId ? moduleTooltipWhenNoVessel : undefined))
      : undefined
  const apaItemTooltip =
    apaSectionDisabled || apaMenuDisabled
      ? (apaContent.tooltip ?? (!activeVesselId ? moduleTooltipWhenNoVessel : undefined))
      : undefined

  const isSpender = isSpenderOnlyRole(activeOrganizationRole)
  const isApprover = isApproverOnlyRole(activeOrganizationRole)

  const operationsNavItemsFull: NavItem[] = [
    {
      title: 'Ops Dashboard',
      url: '/ops/dashboard',
      icon: BarChart3,
      disabled: opsMenuDisabled,
      tooltip: opsItemTooltip,
    },
    {
      title: 'Wallet',
      url: '/ops/wallet',
      icon: Wallet,
      disabled: opsMenuDisabled || !minimumRequiredRole(activeOrganizationRole, Role.VESSEL_ADMIN),
      tooltip: opsItemTooltip,
    },
    {
      title: 'Expenses',
      url: '/ops/transactions',
      icon: Banknote,
      disabled: opsMenuDisabled,
      tooltip: opsItemTooltip,
    },
    {
      title: 'Cards',
      url: '/ops/cards',
      icon: CreditCard,
      disabled: opsMenuDisabled || !minimumRequiredRole(activeOrganizationRole, Role.VESSEL_ADMIN),
      tooltip: opsItemTooltip,
    },
    {
      title: 'Reports',
      url: '/ops/reports',
      icon: FileText,
      disabled: opsMenuDisabled || !minimumRequiredRole(activeOrganizationRole, Role.VESSEL_ADMIN),
      tooltip: opsItemTooltip,
    },
    ...(orgFeatureToggles?.featureToggles?.OPS_CASH === FeatureToggleValue.ENABLED
      ? [
          {
            title: 'Cash',
            url: '/ops/cash',
            icon: PiggyBank,
            disabled: opsMenuDisabled || !minimumRequiredRole(activeOrganizationRole, Role.VESSEL_ADMIN),
            tooltip: opsItemTooltip,
          },
        ]
      : []),
  ]

  const apaNavItemsFull: NavItem[] = [
    {
      title: 'APA Dashboard',
      url: '/apa/dashboard',
      icon: BarChart3,
      disabled: apaSectionDisabled,
      tooltip: apaItemTooltip,
    },
    {
      title: 'Wallet',
      url: '/apa/wallet',
      icon: Wallet,
      disabled: apaMenuDisabled || !minimumRequiredRole(activeOrganizationRole, Role.VESSEL_ADMIN),
      tooltip: apaItemTooltip,
    },
    {
      title: 'APA Owes Operations',
      url: '/apa/apa-owes-ops',
      icon: Coins,
      disabled: apaMenuDisabled || !minimumRequiredRole(activeOrganizationRole, Role.VESSEL_ADMIN),
      tooltip: apaItemTooltip,
    },
    ...(minimumRequiredRole(activeOrganizationRole, Role.VESSEL_ADMIN)
      ? [
          {
            title: 'Charters',
            url: '/apa/charters',
            icon: MapPin,
            disabled: apaSectionDisabled,
            tooltip: apaItemTooltip,
          },
        ]
      : []),
    {
      title: 'Expenses',
      url: '/apa/transactions',
      icon: Banknote,
      disabled: apaSectionDisabled,
      tooltip: apaItemTooltip,
    },
    {
      title: 'Cards',
      url: '/apa/cards',
      icon: CreditCard,
      disabled: apaMenuDisabled || !minimumRequiredRole(activeOrganizationRole, Role.VESSEL_ADMIN),
      tooltip: apaItemTooltip,
    },
    {
      title: 'Reports',
      url: '/apa/reports',
      icon: FileText,
      disabled: apaMenuDisabled || !minimumRequiredRole(activeOrganizationRole, Role.VESSEL_ADMIN),
      tooltip: apaItemTooltip,
    },
  ]

  const operationsNavItems: NavItem[] = isSpender
    ? restrictNavItems(operationsNavItemsFull, ['/ops/transactions'])
    : isApprover
      ? restrictNavItems(operationsNavItemsFull, ['/ops/dashboard', '/ops/transactions'])
      : operationsNavItemsFull

  const apaNavItems: NavItem[] = isSpender
    ? restrictNavItems(apaNavItemsFull, ['/apa/transactions'])
    : isApprover
      ? restrictNavItems(apaNavItemsFull, ['/apa/dashboard', '/apa/transactions'])
      : apaNavItemsFull

  const mainSidebarNavItems: NavItem[] = minimumRequiredRole(activeOrganizationRole, Role.VESSEL_ADMIN)
    ? [
        {
          title: 'Vessel Users',
          url: '/vessel-users',
          icon: Users,
          disabled: !activeVesselId,
          tooltip: !activeVesselId ? moduleTooltipWhenNoVessel : undefined,
        },
      ]
    : []

  /**
   * Sidebar sections are only shown if the user has the required role.
   * Section is shown if: ADMIN/OWNER, or (minimum SPENDER and connected account ACTIVE).
   * menuItemsEnabled is true only when the module/connected account is ACTIVE.
   */
  const canShowSection = (moduleMenuItemsEnabled: boolean) =>
    isPrivilegedRole(activeOrganizationRole) ||
    (minimumRequiredRole(activeOrganizationRole, Role.SPENDER) && moduleMenuItemsEnabled)

  const sidebarSections: {
    color: 'orange' | 'primary'
    title: string
    content: { cta: ModuleCTAContent | null }
    items: NavItem[]
    onActivate?: () => void
  }[] = [
    ...(canShowSection(opsContent.menuItemsEnabled)
      ? [
          {
            color: 'orange' as const,
            title: 'Operations',
            content: opsContent,
            items: operationsNavItems,
            onActivate: () => {
              setActivateModuleType(AwxConnectedAccountType.OPERATIONS)
            },
          },
        ]
      : []),
    ...(canShowSection(apaContent.menuItemsEnabled)
      ? [
          {
            color: 'primary' as const,
            title: 'APA Management',
            content: apaContent,
            items: apaNavItems,
            onActivate: () => {
              setActivateModuleType(AwxConnectedAccountType.APA)
            },
          },
        ]
      : []),
  ]

  const _Navbar = ({ className }: { className?: string }) => {
    return (
      <Navbar className={className}>
        <Link to="/">
          <img
            src={AnkorLogo}
            className="size-8"
          />
        </Link>
        <NavbarSpacer />
        <NavbarSection>
          <NavbarDivider />
          <UserAvatarMenu />
        </NavbarSection>
      </Navbar>
    )
  }

  const _Sidebar = () => (
    <Sidebar className="lg:pt-14 lg:border-r border-gray-200 dark:border-gray-600">
      <SidebarHeader>
        <VesselDropdown />
      </SidebarHeader>
      <SidebarBody className="p-2 space-y-3">
        {sidebarSections.map(({ color, title, content, items, onActivate }) => (
          <SidebarNavSection
            key={title}
            title={title}
            color={color}
            items={items}
            currentPath={location.pathname}
            cta={
              <SectionCta
                cta={content.cta}
                color={color}
                onActivate={onActivate}
              />
            }
          />
        ))}

        {mainSidebarNavItems.length > 0 ? (
          <Divider
            className="border-gray-200 dark:border-gray-600"
            soft
          />
        ) : null}

        <SidebarNavLinks
          items={mainSidebarNavItems}
          currentPath={location.pathname}
          color="primary"
        />

        <Tooltip
          id="sidebar-disabled-tooltip"
          place="bottom"
          className="max-w-48! text-xs! wrap-break-word! whitespace-normal!"
        />
      </SidebarBody>

      <SidebarFooter className="p-2">
        <ExpandableMenu>
          <ExpandableMenuPanel>
            <ExpandableMenuItem onClick={() => setIsOrganizationsModalOpen(true)}>
              <ArrowLeftRight data-slot="icon" />
              Switch Organization
            </ExpandableMenuItem>
            {minimumRequiredRole(activeOrganizationRole, Role.ADMIN) && (
              <ExpandableMenuItem
                to="/users/list"
                current={location.pathname.startsWith('/users/list')}
              >
                <Users data-slot="icon" />
                Organization Users
              </ExpandableMenuItem>
            )}
            {minimumRequiredRole(activeOrganizationRole, Role.ADMIN) && (
              <ExpandableMenuItem
                to="/apa/categories"
                current={location.pathname.startsWith('/apa/categories')}
                disabled={apaMenuDisabled}
              >
                <Tags data-slot="icon" />
                APA Categories
              </ExpandableMenuItem>
            )}
            {minimumRequiredRole(activeOrganizationRole, Role.ADMIN) && (
              <ExpandableMenuItem
                to="/ops/categories"
                current={location.pathname.startsWith('/ops/categories')}
                disabled={opsMenuDisabled}
              >
                <Tags data-slot="icon" />
                OPS Categories
              </ExpandableMenuItem>
            )}
            {minimumRequiredRole(activeOrganizationRole, Role.ADMIN) && (
              <ExpandableMenuItem
                to="/settings/integrations"
                current={location.pathname.startsWith('/settings/integrations')}
              >
                <Plug data-slot="icon" />
                Integrations
              </ExpandableMenuItem>
            )}
          </ExpandableMenuPanel>
          <ExpandableMenuTrigger aria-label="Organization Settings">
            <Building className="size-7 shrink-0" />
            <SidebarLabel>
              <span className="block text-xs uppercase tracking-wider font-normal">Organization</span>
              <span
                data-testid="organization-name"
                className="block text-xs font-bold truncate text-gray-900 dark:text-white"
              >
                {activeOrganization?.name || UNNAMED_ORGANIZATION}
              </span>
            </SidebarLabel>
            <ExpandableMenuChevron />
          </ExpandableMenuTrigger>
        </ExpandableMenu>
      </SidebarFooter>
    </Sidebar>
  )

  return (
    <>
      {/* Desktop navbar only */}
      <_Navbar className="hidden lg:flex fixed top-0 left-0 right-0 h-14 items-center px-4 shadow-sm z-30 gap-4 border-b border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800" />

      <SidebarLayout
        navbar={<_Navbar />}
        sidebar={<_Sidebar />}
      >
        {/* Main content with proper sidebar spacing */}
        <main className="flex-1 transition-all lg:py-8 duration-200 ease-in-out peer-data-[state=collapsed]:ml-[calc(var(--sidebar-width-icon))] peer-data-[state=expanded]:ml-(--sidebar-width)">
          {children}
        </main>
      </SidebarLayout>

      {isOrganizationsModalOpen ? (
        <Suspense fallback={null}>
          <OrganizationsModal
            isOpen
            onClose={() => setIsOrganizationsModalOpen(false)}
          />
        </Suspense>
      ) : null}

      {activateModuleType ? (
        <Suspense fallback={null}>
          <ActivateModuleModal
            isOpen={true}
            onClose={() => {
              setActivateModuleType(null)
              refreshConnectedAccounts()
            }}
            moduleType={activateModuleType}
          />
        </Suspense>
      ) : null}
    </>
  )
}

export default Layout
